import { Form, FormField, IFormField, RequiredValidator } from '@thinknimble/tn-forms'
import { SelectOption } from '../base-model'

/**
 * Hidden + select + text fields for the Add Property modal. ``owner``,
 * ``country``, and ``provider`` are tracked as ``SelectOption`` so they can
 * bind cleanly to ``<CustomSelect>``. Numeric counts are kept as strings in
 * the form layer and converted at submit time — the raw ``<input
 * type="number">`` value is a string, and tn-forms stores exactly what the
 * input reports.
 */
export type AddPropertyFormInputs = {
  // Use `_name` to avoid colliding with the static `Function.name` property
  // on the Form class constructor.
  _name: IFormField<string>
  country: IFormField<SelectOption | undefined>
  street: IFormField<string>
  city: IFormField<string>
  stateOrRegion: IFormField<string>
  postalCode: IFormField<string>
  latitude: IFormField<string>
  longitude: IFormField<string>
  placeId: IFormField<string>
  bedroomCnt: IFormField<string>
  bathroomCnt: IFormField<string>
  owner: IFormField<string>
}

export class AddPropertyForm extends Form<AddPropertyFormInputs> {
  static _name = FormField.create({
    label: 'Property name',
    placeholder: 'e.g. Sunset Villa',
    type: 'text',
    value: '',
    validators: [new RequiredValidator({ message: 'Please enter a property name' })],
  })

  static country = FormField.create({
    label: 'Country',
    placeholder: 'Select a country',
    type: 'text',
    validators: [new RequiredValidator({ message: 'Please select a country' })],
  })

  static street = FormField.create({
    label: 'Street',
    placeholder: 'Street address',
    type: 'text',
    value: '',
    validators: [],
  })

  static city = FormField.create({
    label: 'City',
    placeholder: 'City',
    type: 'text',
    value: '',
    validators: [],
  })

  static stateOrRegion = FormField.create({
    label: 'State / Region',
    placeholder: 'State or region',
    type: 'text',
    value: '',
    validators: [],
  })

  static postalCode = FormField.create({
    label: 'Postal code',
    placeholder: 'Postal code',
    type: 'text',
    value: '',
    validators: [],
  })

  static latitude = FormField.create({
    label: 'Latitude',
    type: 'hidden',
    value: '',
    validators: [],
  })

  static longitude = FormField.create({
    label: 'Longitude',
    type: 'hidden',
    value: '',
    validators: [],
  })

  static placeId = FormField.create({
    label: 'Place ID',
    type: 'hidden',
    value: '',
    validators: [],
  })

  static bedroomCnt = FormField.create({
    label: 'Bedrooms',
    placeholder: '0',
    type: 'number',
    value: '',
    validators: [],
  })

  static bathroomCnt = FormField.create({
    label: 'Bathrooms',
    placeholder: '0',
    type: 'number',
    value: '',
    validators: [],
  })

  static owner = FormField.create({
    label: 'Owner organization',
    type: 'hidden',
    value: '',
    validators: [new RequiredValidator({ message: 'Owner organization is required' })],
  })
}

export type TAddPropertyForm = AddPropertyForm & AddPropertyFormInputs

/**
 * Form for editing an existing property in the PropertyBasicInfoCard.
 * All fields are optional (the user may only change a subset on any given
 * edit), except for ``_name`` which is always required.
 */
export type EditPropertyFormInputs = {
  _name: IFormField<string>
  description: IFormField<string>
  street: IFormField<string>
  city: IFormField<string>
  stateOrRegion: IFormField<string>
  country: IFormField<string>
  postalCode: IFormField<string>
  latitude: IFormField<string>
  longitude: IFormField<string>
  placeId: IFormField<string>
  bedroomCnt: IFormField<string>
  bathroomCnt: IFormField<string>
  checkInTime: IFormField<string>
  checkOutTime: IFormField<string>
  timezone: IFormField<string>
}

export class EditPropertyForm extends Form<EditPropertyFormInputs> {
  static _name = FormField.create({
    label: 'Property name',
    placeholder: 'e.g. Sunset Villa',
    type: 'text',
    value: '',
    validators: [new RequiredValidator({ message: 'Please enter a property name' })],
  })

  static description = FormField.create({
    label: 'Description',
    placeholder: 'A short description of the property',
    type: 'text',
    value: '',
    validators: [],
  })

  static street = FormField.create({
    label: 'Street',
    placeholder: 'Street address',
    type: 'text',
    value: '',
    validators: [],
  })

  static city = FormField.create({
    label: 'City',
    placeholder: 'City',
    type: 'text',
    value: '',
    validators: [],
  })

  static stateOrRegion = FormField.create({
    label: 'State / Region',
    placeholder: 'State or region',
    type: 'text',
    value: '',
    validators: [],
  })

  static country = FormField.create({
    label: 'Country',
    placeholder: 'e.g. CO, US',
    type: 'text',
    value: '',
    validators: [],
  })

  static postalCode = FormField.create({
    label: 'Postal code',
    placeholder: 'Postal code',
    type: 'text',
    value: '',
    validators: [],
  })

  static latitude = FormField.create({
    label: 'Latitude',
    type: 'hidden',
    value: '',
    validators: [],
  })

  static longitude = FormField.create({
    label: 'Longitude',
    type: 'hidden',
    value: '',
    validators: [],
  })

  static placeId = FormField.create({
    label: 'Place ID',
    type: 'hidden',
    value: '',
    validators: [],
  })

  static bedroomCnt = FormField.create({
    label: 'Bedrooms',
    placeholder: '0',
    type: 'number',
    value: '',
    validators: [],
  })

  static bathroomCnt = FormField.create({
    label: 'Bathrooms',
    placeholder: '0',
    type: 'number',
    value: '',
    validators: [],
  })

  static checkInTime = FormField.create({
    label: 'Check-in time',
    type: 'time',
    value: '',
    validators: [],
  })

  static checkOutTime = FormField.create({
    label: 'Check-out time',
    type: 'time',
    value: '',
    validators: [],
  })

  // Empty = let the server derive it from the address.
  static timezone = FormField.create({
    label: 'Timezone',
    type: 'text',
    value: '',
    validators: [],
  })
}

export type TEditPropertyForm = EditPropertyForm & EditPropertyFormInputs

export type DuplicatePropertyFormInputs = {
  // `_name`: see AddPropertyFormInputs.
  _name: IFormField<string>
}

export class DuplicatePropertyForm extends Form<DuplicatePropertyFormInputs> {
  static _name = FormField.create({
    label: 'New property name',
    placeholder: 'e.g. Room 2',
    type: 'text',
    value: '',
    validators: [new RequiredValidator({ message: 'Please enter a property name' })],
  })
}
export type TDuplicatePropertyForm = DuplicatePropertyForm & DuplicatePropertyFormInputs
